import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { lazy, Suspense, useState } from "react";
import SpinnerCentered from "./components/manager/spinner-centered";
import "./App.css";
import { AuthUserProvider } from "./context/auth.context";

import ViewerLayout from "./pages/Viewer/_layout";
import ViewerPage from "./pages/Viewer";
import { PageContext } from "./context/page.context";

// Lazy Load Layouts
const LazyManagerLayout = lazy(() => import("./pages/manager/_layout"));
const LazyPanelLayout = lazy(() => import("./pages/manager/Panel/_layout"));
const LazyAuthenticationLayout = lazy(
  () => import("./pages/manager/Authentication/_layout")
);
const LazyBrandLayout = lazy(
  () => import("./pages/manager/Panel/Brand/_layout")
);
const LazyExperienceLayout = lazy(
  () => import("./pages/manager/Panel/Brand/experience/_layout")
);

// Public Stats Pages
const LazyStatsLayout = lazy(() => import("./pages/manager/Stats/_layout"));
const LazyExperienceStatsPage = lazy(() => import("./pages/manager/Stats"));
// Lazy Load Pages
const LazySignInPage = lazy(() => import("./pages/manager/Authentication"));
const LazyUserAccountPage = lazy(
  () => import("./pages/manager/Panel/UserAccount")
);
const LazySystemSettingsPage = lazy(
  () => import("./pages/manager/Panel/SystemSettings")
);
const LazySupportPage = lazy(() => import("./pages/manager/Panel/Support"));
const LazyTenantUsersPage = lazy(
  () => import("./pages/manager/Panel/TenantUsers")
);

const LazyBrandsPage = lazy(() => import("./pages/manager/Panel"));

const LazyBrandIndexPage = lazy(
  () => import("./pages/manager/Panel/Brand/index")
);
const LazyBrandUsersPage = lazy(
  () => import("./pages/manager/Panel/Brand/BrandUsers")
);
const LazySetsPage = lazy(
  () => import("./pages/manager/Panel/Brand/Sets/index")
);
const LazySurveysPage = lazy(
  () => import("./pages/manager/Panel/Brand/Surveys/index")
);

const LazyStoresPage = lazy(
  () => import("./pages/manager/Panel/Brand/Stores/index")
);
const LazyStorePage = lazy(
  () => import("./pages/manager/Panel/Brand/Stores/Store")
);

// Experience Admin Pages
const LazyExperienceVirtualObjectsPage = lazy(
  () =>
    import("./pages/manager/Panel/Brand/experience/ExperienceVirtualObjects")
);
const LazyQrCodeSchedulerPage = lazy(
  () => import("./pages/manager/Panel/Brand/experience/QrCodeScheduler")
);
const LazyThemePage = lazy(
  () => import("./pages/manager/Panel/Brand/experience/Theme")
);
const LazyAnalyticsPage = lazy(
  () => import("./pages/manager/Panel/Brand/experience/Analytics")
);
const LazyPagesPage = lazy(
  () => import("./pages/manager/Panel/Brand/experience/Pages")
);
const LazyScenesPage = lazy(
  () => import("./pages/manager/Panel/Brand/experience/Scenes")
);
const LazyAssetsPage = lazy(
  () => import("./pages/manager/Panel/Brand/experience/Assets")
);

const LazyNotFoundPage = lazy(() => import("./pages/NotFound"));
const LazyLogoutPage = lazy(() => import("./pages/manager/Logout"));

const router = createBrowserRouter([
  {
    path: "/manager",
    element: (
      <Suspense fallback={<SpinnerCentered />}>
        <LazyManagerLayout />
      </Suspense>
    ),
    children: [
      /**
       * Logout
       */
      {
        path: "logout",
        element: (
          <Suspense fallback={<SpinnerCentered />}>
            <LazyLogoutPage />
          </Suspense>
        ),
      },
      /**
       * Authentication
       */
      {
        path: "authentication?",
        element: (
          <Suspense fallback={<SpinnerCentered />}>
            <LazyAuthenticationLayout />
          </Suspense>
        ),
        children: [
          {
            path: "sign-in?",
            element: (
              <Suspense fallback={<SpinnerCentered />}>
                <LazySignInPage />
              </Suspense>
            ),
          },
        ],
      },
      /**
       * Stats
       */ {
        path: "stats?",
        element: (
          <Suspense fallback={<SpinnerCentered />}>
            <LazyStatsLayout />
          </Suspense>
        ),
        children: [
          {
            path: "experience/:experienceId",
            element: (
              <Suspense fallback={<SpinnerCentered />}>
                <LazyExperienceStatsPage />
              </Suspense>
            ),
          },
        ],
      },
      /**
       * Panel
       */
      {
        path: "panel",
        element: (
          <Suspense fallback={<SpinnerCentered />}>
            <LazyPanelLayout />
          </Suspense>
        ),
        children: [
          {
            path: "account",
            element: (
              <Suspense fallback={<SpinnerCentered />}>
                <LazyUserAccountPage />
              </Suspense>
            ),
          },
          {
            path: "settings",
            element: (
              <Suspense fallback={<SpinnerCentered />}>
                <LazySystemSettingsPage />
              </Suspense>
            ),
          },
          {
            path: "support",
            element: (
              <Suspense fallback={<SpinnerCentered />}>
                <LazySupportPage />
              </Suspense>
            ),
          },
          {
            path: "tenant-users",
            element: (
              <Suspense fallback={<SpinnerCentered />}>
                <LazyTenantUsersPage />
              </Suspense>
            ),
          },
          {
            path: "brands?",
            element: (
              <Suspense fallback={<SpinnerCentered />}>
                <LazyBrandsPage />
              </Suspense>
            ),
          },
          /**
           *  Brand
           */
          {
            path: "brands/:brandId",
            element: (
              <Suspense fallback={<SpinnerCentered />}>
                <LazyBrandLayout />
              </Suspense>
            ),
            children: [
              {
                path: "",
                element: (
                  <Suspense fallback={<SpinnerCentered />}>
                    <LazyBrandIndexPage />
                  </Suspense>
                ),
              },
              {
                path: "brand-users",
                element: (
                  <Suspense fallback={<SpinnerCentered />}>
                    <LazyBrandUsersPage />
                  </Suspense>
                ),
              },
              {
                path: "sets?",
                element: (
                  <Suspense fallback={<SpinnerCentered />}>
                    <LazySetsPage />
                  </Suspense>
                ),
              },
              {
                path: "surveys?",
                element: (
                  <Suspense fallback={<SpinnerCentered />}>
                    <LazySurveysPage />
                  </Suspense>
                ),
              },
              {
                path: "stores?",
                element: (
                  <Suspense fallback={<SpinnerCentered />}>
                    <LazyStoresPage />
                  </Suspense>
                ),
              },
              {
                path: "stores/:storeId",
                element: (
                  <Suspense fallback={<SpinnerCentered />}>
                    <LazyStorePage />
                  </Suspense>
                ),
              },
              /**
               * Experience
               */
              {
                path: "experience/:experienceId",
                element: (
                  <Suspense fallback={<SpinnerCentered />}>
                    <LazyExperienceLayout />
                  </Suspense>
                ),
                children: [
                  {
                    path: "scenes?",
                    element: (
                      <Suspense fallback={<SpinnerCentered />}>
                        <LazyScenesPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "analytics",
                    element: (
                      <Suspense fallback={<SpinnerCentered />}>
                        <LazyAnalyticsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "pages",
                    element: (
                      <Suspense fallback={<SpinnerCentered />}>
                        <LazyPagesPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "experience-virtual-objects",
                    element: (
                      <Suspense fallback={<SpinnerCentered />}>
                        <LazyExperienceVirtualObjectsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "qr-code-scheduler",
                    element: (
                      <Suspense fallback={<SpinnerCentered />}>
                        <LazyQrCodeSchedulerPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "theme",
                    element: (
                      <Suspense fallback={<SpinnerCentered />}>
                        <LazyThemePage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "assets",
                    element: (
                      <Suspense fallback={<SpinnerCentered />}>
                        <LazyAssetsPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/",
    element: <ViewerLayout />,
    errorElement: (
      <Suspense fallback={<SpinnerCentered />}>
        <LazyNotFoundPage />
      </Suspense>
    ),
    children: [
      {
        path: "", //catch all which should show a default experience
        element: <ViewerPage />,
      },
      {
        path: ":placementId",
        element: <ViewerPage />,
      },
    ],
  },
]);

function App() {
  const [pageTitle, setPageTitle] = useState<string>("");

  return (
    <AuthUserProvider>
      <PageContext.Provider value={{ pageTitle, setPageTitle }}>
        <RouterProvider router={router} />
      </PageContext.Provider>
    </AuthUserProvider>
  );
}

export default App;
